import React, { useCallback } from 'react'
import { MenuEdit, MenuItem, MenuItemSeperator } from '../../../../../../shared/menu/menu'
import { IconActivityLog, IconAnalytic, IconAssessment, IconAuthLogin, IconAuthSession, IconContent, IconDelete, IconGroup, IconPurge } from '../../../../../../shared/icon/icon'
import { useAuditCapabilities } from '../../../../../../shared/audit/use-audit-capabilities'
import { useRoleOrgGroupUser, useRoleOrgNodeUser, useRoleOrgUser } from '../../../../../../role/hook/role-org'

// One user's menu, shared by the Users grid's row menu and the user page's
// header menu so the two can't drift: the same items, in the same order,
// with the same permissions. The row menu adds Edit first ({ edit: true }),
// which opens the user page. Paths are absolute, so the page menu works the
// same from any of the user's sub-pages.
//
// Removing someone from the organization is never the menu's primary
// action (owner report 2026-10-29: a big blue "Delete" on the person page):
// it is the last item, in red, after a line, and says what it does. The
// person page passes onRemove (its own confirmation); elsewhere it opens
// the remove page. A removed member's menu ({ removed: true }) leaves out
// the group and course editors (their groups and courses are read-only
// until a restore) and offers "delete permanently" instead (onPurge).
//
// Returns (userID, { edit, removed, onRemove, onPurge }) => [elements], to
// pass to <Menu> as children (Menu reads each child's props, so the items
// can't be wrapped).
export const REMOVE_TEXT = 'remove from organization'
export const PURGE_TEXT = 'delete permanently'

export const useUserMenuItems = () => {
  const roleOrgUser = useRoleOrgUser()
  const roleOrgNodeUser = useRoleOrgNodeUser()
  const roleOrgGroupUser = useRoleOrgGroupUser()
  const caps = useAuditCapabilities()

  return useCallback(
    (userID, { edit = false, removed = false, onRemove, onPurge } = {}) => {
      const base = `/org/user/list/edit/${userID}`
      const canRemove = !removed && !!roleOrgUser.accessDelete
      const canPurge = removed && !!onPurge && !!roleOrgUser.accessDelete
      const list = [
        <MenuItem key="contents" Icon={IconContent} text="contents" to={`${base}/content`} show={!removed && roleOrgNodeUser.accessRead} />,
        <MenuItem key="groups" Icon={IconGroup} text="groups" to={`${base}/group`} show={!removed && roleOrgGroupUser.accessRead} />,
        <MenuItem key="analytics-custom" Icon={IconAnalytic} text="analytics custom" to={`${base}/analytic-custom`} show={roleOrgGroupUser.accessRead} />,
        <MenuItem key="analytics-general" Icon={IconAnalytic} text="analytics general" to={`${base}/analytic-general`} show={roleOrgGroupUser.accessRead} />,
        <MenuItem key="assessments" Icon={IconAssessment} text="assessments" to={`${base}/assessment-reports`} show={roleOrgGroupUser.accessRead} />,
        <MenuItem key="auth-sessions" Icon={IconAuthSession} text="auth sessions" to={`${base}/auth-session`} show={roleOrgGroupUser.accessRead} />,
        <MenuItem key="sign-in-history" Icon={IconAuthLogin} text="sign-in history" to={`${base}/activity?cat=sign_ins`} show={caps.canAudit} />,
        <MenuItem key="activity" Icon={IconActivityLog} text="activity" to={`${base}/activity`} show={caps.canAudit} />,
        <MenuItemSeperator key="danger-line" show={canRemove || canPurge} />,
        <MenuItem key="remove" Icon={IconDelete} text={REMOVE_TEXT} danger pin={false}
          to={onRemove ? undefined : `${base}/delete`} onClick={onRemove} show={canRemove} />,
        <MenuItem key="purge" Icon={IconPurge} text={PURGE_TEXT} danger pin={false} onClick={onPurge} show={canPurge} />
      ]
      return edit ? [<MenuEdit key="edit" to={base} show={roleOrgUser.accessRead} />, ...list] : list
    },
    [roleOrgUser, roleOrgNodeUser, roleOrgGroupUser, caps]
  )
}
